import * as React from "react";
import { useState, useEffect } from "react";
import toast from "react-hot-toast";
import {
  createStep2Schema,
  validateStep,
} from "../../../validation/abstractSchema";
import { toastMultilineErrors } from "../../../util";

interface Step2Props {
  formData: any;
  setFormData: (data: any) => void;
  currentUser: any;
  maxCorrespondingAuthors?: number;
  submissionMode: string;
  onNext: () => void;
  onBack: () => void;
}

const Step2: React.FC<Step2Props> = ({
  formData,
  setFormData,
  currentUser,
  maxCorrespondingAuthors = 2,
  submissionMode,
  onNext,
  onBack,
}) => {
  /*******************************
   * State variables
   *******************************/
  const [institutionInput, setInstitutionInput] = useState("");
  const [institutions, setInstitutions] = useState(formData.institutions || []);
  const [authors, setAuthors] = useState(formData.authors || []);

  /*******************************
   * Effects
   *******************************/
  // Update formData whenever institutions or authors change
  useEffect(() => {
    setFormData((prev: any) => ({
      ...prev,
      institutions,
      authors,
    }));
  }, [institutions, authors, setFormData]);

  /*******************************
   * Handlers
   *******************************/
  const handleAddInstitution = () => {
    if (institutionInput.trim() === "") {
      toast.error("Please enter an institution name");
      return;
    }

    if (institutions.includes(institutionInput.trim())) {
      toast.error("This institution has already been added");
      return;
    }

    setInstitutions([...institutions, institutionInput.trim()]);
    setInstitutionInput("");
  };

  const handleRemoveInstitution = (index: number) => {
    if (index === 0 && currentUser?.institution) {
      toast.error("Cannot remove current user's institution");
      return;
    }

    const newInstitutions = institutions.filter(
      (_: any, i: number) => i !== index,
    );
    setInstitutions(newInstitutions);

    // Remove this institution from all authors
    const updatedAuthors = authors.map((author: any) => ({
      ...author,
      abstract_author_institutions_attributes:
        author.abstract_author_institutions_attributes.filter(
          (inst: any) => inst.abstract_institution_id !== index,
        ),
    }));
    setAuthors(updatedAuthors);
  };

  const handleAddAuthor = () => {
    const newAuthor = {
      first_name: "",
      last_name: "",
      abstract_author_institutions_attributes: [
        { abstract_institution_id: "" },
      ],
      corresponding_email: "",
      is_corresponding: false,
      position: "",
    };
    setAuthors([...authors, newAuthor]);
  };

  const handleRemoveAuthor = (index: number) => {
    if (index === 0) {
      toast.error("Cannot remove first author (current user)");
      return;
    }

    const newAuthors = authors.filter((_: any, i: number) => i !== index);
    setAuthors(newAuthors);
  };

  const handleAuthorChange = (index: number, field: string, value: any) => {
    const updatedAuthors = [...authors];
    updatedAuthors[index] = {
      ...updatedAuthors[index],
      [field]: value,
    };
    setAuthors(updatedAuthors);
  };

  const handleAddInstitutionField = (authorIndex: number) => {
    const updatedAuthors = [...authors];
    const currentInstitutions =
      updatedAuthors[authorIndex].abstract_author_institutions_attributes || [];

    updatedAuthors[authorIndex].abstract_author_institutions_attributes = [
      ...currentInstitutions,
      { abstract_institution_id: "" },
    ];
    setAuthors(updatedAuthors);
  };

  const handleRemoveInstitutionField = (
    authorIndex: number,
    instIndex: number,
  ) => {
    const updatedAuthors = [...authors];
    const institutionsForAuthor =
      updatedAuthors[authorIndex].abstract_author_institutions_attributes || [];

    if (authorIndex === 0 && instIndex === 0 && currentUser?.institution) {
      toast.error("Cannot remove current user's institution");
      return;
    }

    if (institutionsForAuthor.length <= 1) {
      return;
    }

    updatedAuthors[authorIndex].abstract_author_institutions_attributes =
      institutionsForAuthor.filter((_: any, idx: number) => idx !== instIndex);

    setAuthors(updatedAuthors);
  };

  const handleCorrespondingChange = (index: number, checked: boolean) => {
    const correspondingCount = authors.filter(
      (a: any) => a.is_corresponding,
    ).length;

    // TODO: Move the corresponding author limit to CMS adjustable setting
    if (checked && correspondingCount >= maxCorrespondingAuthors) {
      toast.error(
        `Maximum ${maxCorrespondingAuthors} corresponding authors allowed`,
      );
      return;
    }

    const updatedAuthors = [...authors];
    updatedAuthors[index].is_corresponding = checked;
    if (!checked) {
      updatedAuthors[index].corresponding_email = "";
    }
    setAuthors(updatedAuthors);
  };

  const handlePositionChange = (index: number, newPosition: string) => {
    const updatedAuthors = [...authors];
    updatedAuthors[index].position = newPosition;
    setAuthors(updatedAuthors);
  };

  const handleNext = () => {
    const stepData = {
      institutions,
      authors,
    };

    const validation = validateStep(
      stepData,
      createStep2Schema(maxCorrespondingAuthors),
    );
    console.log(validation, "validation---");
    if (!validation.isValid) {
      const errorMessages = Object.values(validation.errors || {});
      toastMultilineErrors(errorMessages);
      return;
    }

    setFormData((prev: any) => ({
      ...prev,
      institutions,
      authors,
    }));

    onNext();
  };

  return (
    <div className="relative w-full bg-white">
      <div className="flex flex-col items-start w-full space-y-10">
        {/* Author / Co-authors Section */}
        <div className="flex flex-col w-full space-y-2">
          <p className="text-lg font-bold leading-8 text-gray-900">
            Author / Co-authors
          </p>
          <p className="text-sm font-normal leading-7 text-justify text-gray-900">
            <span>
              Each submission will automatically set the{" "}
              <strong>submitter as the presenter</strong>. Presenter is
              non-changeable. Please fill in the{" "}
            </span>
            <span className="font-bold">department and institution</span>
            <span>
              {" "}
              for all authors. You will be able to select the corresponding
              institution once each author's name has been entered. Only one
              main author is allowed.{" "}
            </span>
            <span className="font-bold">
              You may adjust the display order of co-authors according to their
              numbering
            </span>
            . A maximum of {maxCorrespondingAuthors} corresponding authors can
            be assigned.
          </p>
        </div>

        {/* Institution/Affiliation Section */}
        <div className="flex flex-col w-full space-y-4">
          <div className="flex flex-col space-y-4">
            <p className="text-lg font-normal leading-7 text-gray-900">
              Institution / Affiliation*
            </p>
            <div className="flex items-start w-full space-x-4">
              <input
                type="text"
                value={institutionInput}
                onChange={(e) => setInstitutionInput(e.target.value)}
                className="flex-1 px-4 py-2 text-sm font-normal leading-5 text-gray-900 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter Institution / Organisation name"
              />
              <button
                onClick={handleAddInstitution}
                className="flex items-center h-10 px-6 py-2 transition-colors bg-blue-900 rounded cursor-pointer hover:bg-blue-800"
              >
                <div className="flex items-center space-x-2">
                  <svg className="w-6 h-6" viewBox="0 0 25 25" fill="none">
                    <path
                      d="M12.5 5.5V19.5M5.5 12.5H19.5"
                      stroke="white"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <span className="text-sm font-normal leading-5 text-white">
                    Add Institution
                  </span>
                </div>
              </button>
            </div>
          </div>

          {/* Institution Tags */}
          {institutions.length > 0 && (
            <div className="flex flex-wrap items-center w-full gap-2">
              {institutions.map((inst: string, index: number) => (
                <div
                  key={index}
                  className="flex items-center px-3 py-2 border border-gray-200 rounded-lg gap-2"
                >
                  <span className="text-sm font-normal leading-5 text-gray-500">
                    {inst}
                  </span>
                  {!(index === 0 && currentUser?.institution) && (
                    <button
                      onClick={() => handleRemoveInstitution(index)}
                      className="cursor-pointer"
                    >
                      <svg className="w-3 h-3" viewBox="0 0 12 12" fill="none">
                        <path
                          d="M9 3L3 9M3 3L9 9"
                          stroke="#6B7280"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Authors Section */}
        <div className="flex flex-col w-full space-y-6">
          <p className="text-lg font-normal leading-7 text-gray-900">Author</p>

          <div className="flex flex-col w-full space-y-6">
            {authors.map((author: any, authorIndex: number) => (
              <div
                key={authorIndex}
                className="flex flex-col w-full p-6 space-y-4 border border-gray-300 rounded-lg bg-gray-50 bg-opacity-66"
              >
                {/* First and Last Name */}
                <div className="flex w-full space-x-4">
                  <div className="flex flex-col flex-1 space-y-1">
                    <label className="text-sm font-normal leading-5 text-gray-500">
                      First Name/Given Name*
                    </label>
                    <input
                      type="text"
                      value={author.first_name}
                      onChange={(e) =>
                        handleAuthorChange(
                          authorIndex,
                          "first_name",
                          e.target.value,
                        )
                      }
                      className="border border-gray-300 rounded-md px-4 py-2 text-sm font-normal leading-5 focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                      placeholder="First Name"
                    />
                  </div>
                  <div className="flex flex-col flex-1 space-y-1">
                    <label className="text-sm font-normal leading-5 text-gray-500">
                      Last Name/Surname/Family Name*
                    </label>
                    <input
                      type="text"
                      value={author.last_name}
                      onChange={(e) =>
                        handleAuthorChange(
                          authorIndex,
                          "last_name",
                          e.target.value,
                        )
                      }
                      className="border border-gray-300 rounded-md px-4 py-2 text-sm font-normal leading-5 focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                      placeholder="Last Name"
                    />
                  </div>
                </div>

                {/* Institution Selection */}
                <div className="flex flex-col w-full space-y-1">
                  <label className="text-sm font-normal leading-5 text-gray-500">
                    Institution / Organisation*
                  </label>
                  {author.abstract_author_institutions_attributes.map(
                    (inst: any, instIndex: number) => {
                      const selectedInstitutionsForThisAuthor =
                        author.abstract_author_institutions_attributes
                          .map((i: any) => i.abstract_institution_id)
                          .filter(
                            (id: any) =>
                              id !== "" && id !== null && id !== undefined,
                          );

                      const availableInstitutions = institutions.filter(
                        (institution: string) =>
                          !selectedInstitutionsForThisAuthor.includes(
                            institution,
                          ),
                      );

                      const isLastField =
                        instIndex ===
                        author.abstract_author_institutions_attributes.length -
                          1;
                      const hasAvailableInstitutions =
                        availableInstitutions.length > 0;

                      return (
                        <div
                          key={instIndex}
                          className="flex items-center w-full mb-1 gap-1"
                        >
                          <select
                            value={inst.abstract_institution_id || ""}
                            onChange={(e) => {
                              const updatedAuthors = [...authors];
                              updatedAuthors[
                                authorIndex
                              ].abstract_author_institutions_attributes[
                                instIndex
                              ].abstract_institution_id = e.target.value;
                              setAuthors(updatedAuthors);
                            }}
                            disabled={authorIndex === 0 && instIndex === 0}
                            className={`flex-1 border border-gray-300 rounded-md px-4 py-2 text-sm font-normal leading-5 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 ${
                              authorIndex === 0 && instIndex === 0
                                ? "bg-gray-100 text-gray-400"
                                : "text-gray-700 bg-white"
                            }`}
                          >
                            <option value="">
                              Select Institution / Organisation
                            </option>
                            {institutions.map(
                              (institution: string, idx: number) => {
                                const isCurrentSelection =
                                  inst.abstract_institution_id === institution;
                                const isAlreadySelectedByThisAuthor =
                                  selectedInstitutionsForThisAuthor.includes(
                                    institution,
                                  );

                                if (
                                  isAlreadySelectedByThisAuthor &&
                                  !isCurrentSelection
                                ) {
                                  return null;
                                }

                                return (
                                  <option key={idx} value={institution}>
                                    {institution}
                                  </option>
                                );
                              },
                            )}
                          </select>

                          {author.abstract_author_institutions_attributes
                            .length > 1 &&
                            !(
                              authorIndex === 0 &&
                              instIndex === 0 &&
                              currentUser?.institution
                            ) && (
                              <button
                                type="button"
                                onClick={() =>
                                  handleRemoveInstitutionField(
                                    authorIndex,
                                    instIndex,
                                  )
                                }
                                className="flex items-center justify-center px-3 py-2 transition-colors bg-white border border-gray-300 rounded shrink-0 hover:bg-gray-100"
                              >
                                <svg
                                  className="w-5 h-5"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                >
                                  <path
                                    d="M6 12H18"
                                    stroke="#6B7280"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                              </button>
                            )}

                          {isLastField && hasAvailableInstitutions && (
                            <button
                              type="button"
                              onClick={() =>
                                handleAddInstitutionField(authorIndex)
                              }
                              className="border rounded flex items-center justify-center px-6 py-2 shrink-0 bg-gray-100 border-gray-300"
                            >
                              <div className="flex items-center gap-1.5">
                                <svg
                                  className="w-6 h-6"
                                  viewBox="0 0 25 25"
                                  fill="none"
                                >
                                  <path
                                    d="M12.5 5.5V19.5M5.5 12.5H19.5"
                                    stroke="#6B7280"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                                <span className="text-sm font-normal leading-5 text-gray-700">
                                  Add Institution
                                </span>
                              </div>
                            </button>
                          )}
                        </div>
                      );
                    },
                  )}
                </div>

                {/* Corresponding Author Checkbox and Email */}
                <div className="flex flex-col flex-1 space-y-4">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id={`corresponding-author-${authorIndex}`}
                      checked={author.is_corresponding}
                      onChange={(e) =>
                        handleCorrespondingChange(authorIndex, e.target.checked)
                      }
                      className="w-4 h-4 text-blue-900 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <label
                      htmlFor={`corresponding-author-${authorIndex}`}
                      className="text-sm font-medium leading-5 text-gray-700 cursor-pointer"
                    >
                      Corresponding Author (
                      {authors.filter((a: any) => a.is_corresponding).length}/
                      {maxCorrespondingAuthors})
                    </label>
                  </div>

                  {author.is_corresponding && (
                    <div className="flex flex-col w-full space-y-1">
                      <label className="text-sm font-normal leading-5 text-gray-500">
                        Email
                      </label>
                      <input
                        type="email"
                        value={author.corresponding_email}
                        onChange={(e) =>
                          handleAuthorChange(
                            authorIndex,
                            "corresponding_email",
                            e.target.value.toLowerCase(),
                          )
                        }
                        className="px-4 py-2 text-sm font-normal leading-5 text-gray-900 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="example@email.com"
                      />
                    </div>
                  )}
                </div>
              </div>
            ))}

            {/* Add Authors Button */}
            <button
              onClick={handleAddAuthor}
              className="flex items-center justify-center h-10 px-6 py-2 transition-colors bg-blue-900 rounded cursor-pointer w-fit hover:bg-blue-800"
            >
              <div className="flex items-center space-x-2">
                <svg className="w-6 h-6" viewBox="0 0 25 25" fill="none">
                  <path
                    d="M12.5 5.5V19.5M5.5 12.5H19.5"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span className="text-sm font-normal leading-5 text-white">
                  Add Authors
                </span>
              </div>
            </button>
          </div>
        </div>

        {/* Author Position List */}
        {authors.length > 0 && (
          <div className="flex flex-col w-full space-y-2">
            {authors.map((author: any, index: number) => {
              const selectedPositions = authors
                .filter((_: any, i: number) => i !== index)
                .map((a: any) => a.position)
                .filter(Boolean);

              return (
                <div
                  key={index}
                  className="flex items-center w-full p-6 bg-gray-100 rounded-lg gap-6"
                >
                  <select
                    value={author.position}
                    onChange={(e) =>
                      handlePositionChange(index, e.target.value)
                    }
                    className="px-4 py-2 text-sm font-normal leading-5 text-gray-700 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 w-44"
                  >
                    <option value="">Select Position</option>
                    {!selectedPositions.includes("1") && (
                      <option value="1">Main Author</option>
                    )}
                    {authors.length > 1 &&
                      Array.from({ length: authors.length - 1 }, (_, i) => {
                        const position = (i + 2).toString();
                        if (selectedPositions.includes(position)) {
                          return null;
                        }
                        return (
                          <option key={i + 2} value={position}>
                            Co-Author {i + 1}
                          </option>
                        );
                      })}
                  </select>

                  <div className="flex flex-col flex-1">
                    <div className="flex items-center gap-4">
                      <p className="text-base font-normal leading-7 text-gray-900">
                        {author.first_name} {author.last_name}
                      </p>
                      {author.position === "1" && (
                        <div className="px-3 py-1 bg-blue-100 rounded-2xl">
                          <span className="text-sm font-medium leading-5 text-blue-900">
                            Main Author
                          </span>
                        </div>
                      )}
                      {author.is_corresponding && (
                        <div className="px-3 py-1 bg-blue-100 rounded-2xl">
                          <span className="text-sm font-medium leading-5 text-blue-900">
                            Corresponding Author
                          </span>
                        </div>
                      )}
                    </div>
                    <p className="text-sm font-normal leading-5 text-gray-500">
                      {author.abstract_author_institutions_attributes
                        .map((inst: any) => inst.abstract_institution_id)
                        .filter(Boolean)
                        .join(", ")}
                    </p>
                  </div>

                  {index !== 0 && (
                    <button
                      onClick={() => handleRemoveAuthor(index)}
                      className="cursor-pointer"
                    >
                      <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none">
                        <path
                          d="M19 7L18.1327 19.1425C18.0579 20.1891 17.187 21 16.1378 21H7.86224C6.81296 21 5.94208 20.1891 5.86732 19.1425L5 7M10 11V17M14 11V17M15 7V4C15 3.44772 14.5523 3 14 3H10C9.44772 3 9 3.44772 9 4V7M4 7H20"
                          stroke="#6B7280"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex items-center justify-between w-full">
          <button
            onClick={onBack}
            className="flex items-center px-6 py-2 transition-colors bg-blue-100 rounded cursor-pointer hover:bg-blue-200"
          >
            <div className="flex items-center space-x-2">
              <span className="text-sm font-normal leading-5 text-gray-900">
                ← Abstract Type
              </span>
            </div>
          </button>

          <button
            onClick={handleNext}
            className="flex items-center px-6 py-2 transition-colors bg-blue-900 rounded cursor-pointer hover:bg-blue-800"
          >
            <div className="flex items-center space-x-2">
              <span className="text-sm font-normal leading-5 text-white">
                {submissionMode === "file_upload"
                  ? "Upload Abstract →"
                  : "Abstract Content →"}
              </span>
            </div>
          </button>
        </div>
      </div>
    </div>
  );
};

export default Step2;
